import React, { useState } from 'react';
import { AccountOption } from '../services/authService';

interface AccountSelectorModalProps {
  accounts: AccountOption[];
  onSelect: (accountType: 'team' | 'portal' | 'bridge_club') => void;
  onCancel: () => void;
  loading?: boolean;
}

const AccountSelectorModal: React.FC<AccountSelectorModalProps> = ({
  accounts,
  onSelect,
  onCancel,
  loading = false,
}) => {
  const [rememberChoice, setRememberChoice] = useState(false);

  const getAccountIcon = (type: string) => {
    switch (type) {
      case 'team':
        return '🔧';
      case 'portal':
        return '🏪';
      case 'bridge_club':
        return '🌾';
      default:
        return '👤';
    }
  };

  const getAccountColor = (type: string) => {
    switch (type) {
      case 'team':
        return '#2D5A27';
      case 'portal':
        return '#1d4ed8';
      case 'bridge_club':
        return '#E73C3E';
      default:
        return '#6b7280';
    }
  };

  const handleSelect = (accountType: 'team' | 'portal' | 'bridge_club') => {
    if (rememberChoice) {
      localStorage.setItem('preferred_account_type', accountType);
    }
    onSelect(accountType);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Welcome back!</h2>
          <p style={styles.subtitle}>Which account would you like to access?</p>
        </div>

        <div style={styles.accountList}>
          {accounts.map((account) => (
            <button
              key={account.type}
              onClick={() => handleSelect(account.type)}
              disabled={loading}
              style={{
                ...styles.accountCard,
                borderLeftColor: getAccountColor(account.type),
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              <div style={styles.accountIcon}>
                {getAccountIcon(account.type)}
              </div>
              <div style={styles.accountInfo}>
                <div style={styles.accountLabel}>{account.label}</div>
                <div style={styles.accountSubtitle}>{account.subtitle}</div>
              </div>
              <div style={styles.accountArrow}>→</div>
            </button>
          ))}
        </div>

        <div style={styles.rememberSection}>
          <label style={styles.rememberLabel}>
            <input
              type="checkbox"
              checked={rememberChoice}
              onChange={(e) => setRememberChoice(e.target.checked)}
              style={styles.checkbox}
            />
            Remember my choice
          </label>
        </div>

        <button
          onClick={onCancel}
          style={styles.cancelButton}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxWidth: '420px',
    width: '100%',
    padding: '2rem',
  },
  header: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1f2937',
  },
  subtitle: {
    margin: '0.5rem 0 0',
    fontSize: '0.95rem',
    color: '#6b7280',
  },
  accountList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  accountCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    border: 'none',
    borderLeft: '4px solid #6b7280',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.2s',
    textAlign: 'left',
    width: '100%',
  },
  accountIcon: {
    fontSize: '1.75rem',
    flexShrink: 0,
  },
  accountInfo: {
    flex: 1,
    minWidth: 0,
  },
  accountLabel: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '0.25rem',
  },
  accountSubtitle: {
    fontSize: '0.85rem',
    color: '#6b7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountArrow: {
    fontSize: '1.25rem',
    color: '#9ca3af',
    flexShrink: 0,
  },
  rememberSection: {
    marginTop: '1.5rem',
    textAlign: 'center',
  },
  rememberLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    color: '#4b5563',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  cancelButton: {
    width: '100%',
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default AccountSelectorModal;
